import { useState } from 'react';

import CloseIcon from '@mui/icons-material/Close';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import ButtonBase from '@mui/material/ButtonBase';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import grey from '@mui/material/colors/grey';
import FormControl from '@mui/material/FormControl';
import MenuItem from '@mui/material/MenuItem';
import Select from '@mui/material/Select';
import styled from '@mui/material/styles/styled';
import Tooltip from '@mui/material/Tooltip';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import { useParams } from 'react-router-dom';

import RemoveModal from './RemoveModal';
import { USER_ROLE_LIST } from '../../../constants';
import { useUser } from '../../../contexts/UserContext';
import Image from '../../elements/image/Image';
import Text from '../../elements/text/Text';
import UserTooltip from '../../elements/user-tooltip/UserTooltip';

const StyledCardContent = styled(CardContent)({
  display: 'flex',
  flexDirection: 'column',
  alignItems: 'center',
});

const StyledText = styled(Text)(({ theme }) => ({
  marginTop: theme.spacing(1),
  marginBottom: theme.spacing(1),
  maxWidth: '90%',
}));

const StyledButton = styled(Button)({
  position: 'absolute',
  right: '0px',
  minWidth: 0,
  padding: '0.5em',
  margin: '0.25em',
});

const StyledButtonBase = styled(ButtonBase)({
  borderRadius: 50,
  '&:hover': {
    filter: 'brightness(85%)',
  },
});

const MemberCard = ({ member, isAdmin, onChangeRole, onRemove }) => {
  const { t } = useTranslation();
  const { user } = useUser();
  const { projectId } = useParams();

  const [modal, setModal] = useState(false);

  const handleToggleModal = () => setModal(!modal);
  const handleRemove = async () => {
    await onRemove(member);
    handleToggleModal();
  };

  const currentProject = member.projects.find(
    (project) => project.projectId === projectId
  );

  return (
    <>
      <Card sx={{ position: 'relative' }}>
        {(isAdmin || member.id === user.id) && (
          <Tooltip
            title={t('removeMember', { ns: 'project' })}
            placement="top"
            arrow
          >
            <StyledButton
              variant="text"
              color="inherit"
              aria-label="remove-user"
              onClick={handleToggleModal}
            >
              <CloseIcon fontSize="small" />
            </StyledButton>
          </Tooltip>
        )}
        <StyledCardContent>
          <UserTooltip user={member}>
            <StyledButtonBase>
              <Image
                src={member.icon}
                alt={member.fullName}
                shape="circular"
                size="large"
              />
            </StyledButtonBase>
          </UserTooltip>
          <StyledText noWrap subtitleBold>
            {member.publicName}
          </StyledText>
          {member.projects &&
            member.projects.length > 0 &&
            (isAdmin ? (
              <FormControl
                variant="outlined"
                size="small"
                aria-label="edit-role"
              >
                <Select
                  value={currentProject.role}
                  onChange={(e) => onChangeRole(member, e.target.value)}
                >
                  {USER_ROLE_LIST.map((role) => (
                    <MenuItem key={role.value} value={role.value}>
                      {t(role.label, { ns: 'menus' })}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            ) : (
              <Box
                aria-label="role"
                sx={{
                  border: `solid 1px ${grey[400]}`,
                  bgcolor: grey[200],
                  color: grey[700],
                  borderRadius: '5px',
                  p: `${0.25}em ${1}em`,
                }}
              >
                {currentProject.role}
              </Box>
            ))}
        </StyledCardContent>
      </Card>
      <RemoveModal
        member={member}
        modal={modal}
        toggleModal={handleToggleModal}
        onConfirm={handleRemove}
      />
    </>
  );
};

MemberCard.propTypes = {
  member: PropTypes.object.isRequired,
  isAdmin: PropTypes.bool,
  onChangeRole: PropTypes.func.isRequired,
  onRemove: PropTypes.func.isRequired,
};

export default MemberCard;
